import React, { useEffect, useState } from 'react';
import { adminApi, SepaCountry } from '../api/admin';

const EMPTY_COUNTRY: SepaCountry = {
  ibanCountryCode: '',
  countryName: '',
  category: 'EEA',
  isActive: true,
};

function SepaCountriesPage() {
  const [countries, setCountries] = useState<SepaCountry[]>([]);
  const [form, setForm] = useState<SepaCountry>(EMPTY_COUNTRY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadCountries = async () => {
    try {
      setLoading(true);
      setCountries((await adminApi.getSepaCountries()).data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load SEPA countries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCountries();
  }, []);

  const editCountry = (country: SepaCountry) => {
    setForm(country);
    setError('');
  };

  const saveCountry = async (event: React.FormEvent) => {
    event.preventDefault();
    const code = form.ibanCountryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code) || !form.countryName.trim() || !form.category.trim()) {
      setError('Enter a two-letter country code, country name, and category.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await adminApi.upsertSepaCountry(code, {
        countryName: form.countryName.trim(),
        category: form.category.trim().toUpperCase(),
        isActive: form.isActive,
      });
      setForm(EMPTY_COUNTRY);
      await loadCountries();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save SEPA country');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">SEPA Countries</h1>
        <p className="mt-1 text-sm text-gray-600">Manage countries permitted for SEPA IBAN validation.</p>
      </div>
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      <form onSubmit={saveCountry} className="mb-6 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <input value={form.ibanCountryCode} onChange={e => setForm(current => ({ ...current, ibanCountryCode: e.target.value.toUpperCase() }))} placeholder="Code" maxLength={2} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <input value={form.countryName} onChange={e => setForm(current => ({ ...current, countryName: e.target.value }))} placeholder="Country name" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <input value={form.category} onChange={e => setForm(current => ({ ...current, category: e.target.value.toUpperCase() }))} placeholder="Category" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm(current => ({ ...current, isActive: e.target.checked }))} />
            Active
          </label>
          <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => setForm(EMPTY_COUNTRY)} className="text-sm text-gray-600 hover:text-gray-900">Clear</button>
        </div>
      </form>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Code</th><th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Country</th><th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Category</th><th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">Loading…</td></tr> : countries.map(country => (
              <tr key={country.ibanCountryCode}>
                <td className="px-4 py-3 font-mono text-sm">{country.ibanCountryCode}</td>
                <td className="px-4 py-3 text-sm">{country.countryName}</td>
                <td className="px-4 py-3 text-sm">{country.category}</td>
                <td className="px-4 py-3 text-sm">{country.isActive ? 'Active' : 'Inactive'}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => editCountry(country)} className="text-sm font-medium text-blue-700 hover:text-blue-900">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SepaCountriesPage;
